import { useEffect } from "react";

import { observer } from "mobx-react-lite";
import { useTranslations } from "next-intl";

import { Description } from "@/components/Description";
import { FormButton } from "@/components/form/FormButton";
import { FormFieldError } from "@/components/form/FormField";
import { FormInput } from "@/components/form/FormInput";
import { useForm } from "@/components/form/hooks/useForm";
import { useFormField } from "@/components/form/hooks/useFormField";
import { Title } from "@/components/Title";
import { useStore } from "@/store";
import { twMerge } from "@/utils/twMerge";

import type { TeamFormFields } from "@/models/api/TeamApi";
import type { RefObject } from "react";

export const StepPermalink = observer(function StepPermalink() {
  const { dialog } = useStore();
  const { createTeamDialog } = dialog;
  const t = useTranslations();

  const { formProps, register, serverError, status } = useForm<TeamFormFields>(
    async (_fields, e) => {
      e.preventDefault();
      await createTeamDialog.createTeam();
      createTeamDialog.setStep("avatar");
    },
  );
  const permalinkFormFields = useFormField("permalink", createTeamDialog, register);

  useEffect(() => {
    const ref = permalinkFormFields.inputProps.ref as RefObject<HTMLInputElement | null>;
    if (ref?.current) {
      ref.current.focus();
    }
  }, [permalinkFormFields.inputProps.ref]);

  return (
    <form className={twMerge("flex flex-col")} noValidate {...formProps} autoComplete="off">
      <Title value={t.rich("createTeamDialog.stepPermalink.title")} />
      <Description value={t("createTeamDialog.stepPermalink.description")} />
      <div className="flex flex-col mb-4">
        {/* This hidden input is used to prevent the browser from autocompleting the permalink field in Firefox */}
        <input name={permalinkFormFields.inputProps.name} type="text" className="hidden" />
        <FormInput
          placeholder={t("createTeamDialog.stepPermalink.placeholder")}
          aria-label={t("createTeamDialog.stepPermalink.placeholder")}
          type="text"
          valuePrefix="@"
          autoComplete="off"
          {...permalinkFormFields.inputProps}
        />
        <FormFieldError message={permalinkFormFields.errorMessage || serverError} />
      </div>
      <FormButton
        isBusy={status === "submitting"}
        variant="accent"
        size="sm"
        type="submit"
        className="w-full"
      >
        {t("createTeamDialog.continueButton")}
      </FormButton>
    </form>
  );
});
